import React, { useState } from 'react';
import { OrderSide, EtfClientOrder } from './BaseCalculatedOrders';
import { QuantityUpdate } from './types';
import { formatDate, formatSummaryAmount } from '@/utils/formatters';
import { ORDER_CALC_TYPE_MUTUAL_FUND } from '@/types/client';
import { useAutoHideMessage } from '@/hooks/useAutoHideMessage';
import TableWrapper from './TableWrapper';
import styles from './OrderManagement.module.css';
import shared from '@/components/utils/shared.module.css';
import { Button, Note, Placeholder, SideTag } from '@/components/ui';
import OrderStatusPill, { calculatedOrderStage } from './orderStatus';

interface ClientWiseOrdersSectionProps {
    titlePrefix: string;
    clientBuyOrders: EtfClientOrder[];
    clientSellOrders: EtfClientOrder[];
    /** Rejects if the save fails; resolves to whether the orders were reloaded afterwards. */
    updateClientOrderQuantities: (side: OrderSide, updates: QuantityUpdate[]) => Promise<boolean>;
}

interface QuantityUpdateErrorBody {
    error?: unknown;
    errors?: unknown;
}

interface RowError {
    id: number;
    error: string;
}

const isRowError = (e: unknown): e is RowError => {
    const entry = e as Partial<RowError> | null;
    return typeof entry?.id === 'number' && typeof entry.error === 'string';
};

// The bulk endpoint's per-order reasons, keyed by order id, e.g. "Quantity 16 exceeds available cash quantity 15".
// Malformed entries are dropped, so a bad body falls back to the generic banner instead of throwing in the catch.
const rowErrorsFrom = (err: unknown): Map<number, string> => {
    const errors = ((err ?? {}) as QuantityUpdateErrorBody).errors;
    return new Map(Array.isArray(errors) ? errors.filter(isRowError).map(e => [e.id, e.error]) : []);
};

type SizeField = 'quantity' | 'amount';

/** The field last typed into, kept as raw text so a partial entry like "12." survives re-render. */
interface PendingEdit {
    field: SizeField;
    text: string;
}

// A mutual-fund scheme deals in fractional units; an equity scheme in whole ones.
const isFractional = (order: EtfClientOrder) => order.scheme?.order_calc_type === ORDER_CALC_TYPE_MUTUAL_FUND;

const unitPriceOf = (order: EtfClientOrder): number | null => {
    const quantity = Number(order.quantity);
    const amount = Number(order.amount);
    return quantity > 0 && Number.isFinite(amount) ? amount / quantity : null;
};

// Mirrors the backend, which rounds an amount down to whole units (equity) or 6 places (MF).
const roundDownUnits = (units: number, fractional: boolean) =>
    fractional ? Math.floor(units * 1e6) / 1e6 : Math.floor(units);

/** What the field not being edited would become, so both columns read consistently. */
const derivedValue = (order: EtfClientOrder, edit: PendingEdit, field: SizeField): string => {
    const unitPrice = unitPriceOf(order);
    const entered = parseFloat(edit.text);
    if (unitPrice === null || !Number.isFinite(entered)) return '';
    if (field === 'amount') return (entered * unitPrice).toFixed(2);
    return String(roundDownUnits(entered / unitPrice, isFractional(order)));
};

/** The update to send for an edit, or why it cannot be sent. */
const toUpdate = (order: EtfClientOrder, edit: PendingEdit): QuantityUpdate | string => {
    const value = parseFloat(edit.text);
    if (!Number.isFinite(value) || value <= 0) {
        return `${edit.field === 'quantity' ? 'Quantity' : 'Amount'} must be greater than 0`;
    }
    if (edit.field === 'amount') return { id: order.id, amount: Math.round(value * 100) / 100 };
    if (!isFractional(order) && !Number.isInteger(value)) return 'Quantity must be a whole number';
    return { id: order.id, quantity: value };
};

const withoutIds = <V,>(map: Map<number, V>, ids: number[]): Map<number, V> => {
    const next = new Map(map);
    ids.forEach(id => next.delete(id));
    return next;
};

const ClientWiseOrdersSection: React.FC<ClientWiseOrdersSectionProps> = ({
    titlePrefix,
    clientBuyOrders,
    clientSellOrders,
    updateClientOrderQuantities,
}) => {
    const [selectedBuyClient, setSelectedBuyClient] = useState<Set<number>>(new Set());
    const [selectedSellClient, setSelectedSellClient] = useState<Set<number>>(new Set());
    const [pendingEdits, setPendingEdits] = useState<Map<number, PendingEdit>>(new Map());
    const [updatingQuantities, setUpdatingQuantities] = useState<OrderSide | null>(null);
    const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map());
    // Shown above this section rather than at the page top, next to the rows they describe.
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    useAutoHideMessage(successMessage, () => setSuccessMessage(null));

    const showError = (message: string) => {
        setSuccessMessage(null);
        setWarning(null);
        setError(message);
    };

    const allEmpty = clientBuyOrders.length === 0 && clientSellOrders.length === 0;

    const handleClientSelect = (id: number, side: OrderSide) => {
        const sel = side === 'buy' ? new Set(selectedBuyClient) : new Set(selectedSellClient);
        sel.has(id) ? sel.delete(id) : sel.add(id);
        side === 'buy' ? setSelectedBuyClient(sel) : setSelectedSellClient(sel);
    };

    const handleSizeChange = (id: number, side: OrderSide, field: SizeField, text: string) => {
        setPendingEdits(prev => new Map(prev).set(id, { field, text }));
        setRowErrors(prev => (prev.has(id) ? withoutIds(prev, [id]) : prev));
        // Fixing the last flagged row retires the "fix the highlighted order(s)" banner with it.
        if (rowErrors.size === 1 && rowErrors.has(id)) setError(null);
        // An edited row is selected, so Update picks it up without a separate click.
        const select = (prev: Set<number>) => (prev.has(id) ? prev : new Set(prev).add(id));
        side === 'buy' ? setSelectedBuyClient(select) : setSelectedSellClient(select);
    };

    const handleUpdateQuantities = async (side: OrderSide) => {
        const sel = side === 'buy' ? selectedBuyClient : selectedSellClient;
        const orders = side === 'buy' ? clientBuyOrders : clientSellOrders;
        const changed = orders.filter(o => sel.has(o.id) && pendingEdits.has(o.id));

        if (changed.length === 0) {
            showError('Select orders with a changed quantity or amount to update');
            return;
        }

        const updates: QuantityUpdate[] = [];
        const invalid = new Map<number, string>();
        changed.forEach(order => {
            const update = toUpdate(order, pendingEdits.get(order.id) as PendingEdit);
            typeof update === 'string' ? invalid.set(order.id, update) : updates.push(update);
        });
        if (invalid.size > 0) {
            setRowErrors(prev => new Map([...prev, ...invalid]));
            showError(`Fix the ${invalid.size} highlighted ${titlePrefix} ${side} order(s) before updating`);
            return;
        }

        const changedIds = changed.map(o => o.id);
        setError(null);
        setWarning(null);
        try {
            setUpdatingQuantities(side);
            // One request: the backend checks each client's cash/holding across the whole batch, all or nothing.
            const reloaded = await updateClientOrderQuantities(side, updates);
            setRowErrors(prev => withoutIds(prev, changedIds));
            side === 'buy' ? setSelectedBuyClient(new Set()) : setSelectedSellClient(new Set());
            if (reloaded) {
                setSuccessMessage(`Updated ${changed.length} ${titlePrefix} ${side} order(s)`);
                setPendingEdits(prev => withoutIds(prev, changedIds));
            } else {
                // Saved, but the rows still hold the old sizes: keep the edits so the inputs show what was saved.
                setWarning(`Updated ${changed.length} ${titlePrefix} ${side} order(s), but the orders could not be reloaded — refresh the page to see the new totals`);
            }
        } catch (err) {
            const failed = rowErrorsFrom(err);
            setRowErrors(prev => new Map([...withoutIds(prev, changedIds), ...failed]));
            if (failed.size > 0) {
                showError(`No ${titlePrefix} ${side} orders updated — fix the ${failed.size} highlighted order(s)`);
            } else {
                const message = (err as QuantityUpdateErrorBody | null)?.error;
                showError(`Failed to update ${titlePrefix} ${side} orders${typeof message === 'string' ? `: ${message}` : ''}`);
            }
        } finally {
            setUpdatingQuantities(null);
        }
    };

    const renderSideControls = (side: OrderSide) => {
        const orders = side === 'buy' ? clientBuyOrders : clientSellOrders;
        const sel = side === 'buy' ? selectedBuyClient : selectedSellClient;
        if (orders.length === 0) return null;

        const hasChanges = Array.from(sel).some(id => pendingEdits.has(id));

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <SideTag side={side} colored>{side === 'buy' ? 'Buy' : 'Sell'}</SideTag>
                <span className={styles['toolbar-info']}>{sel.size} of {orders.length} selected</span>
                <Button
                    size="sm"
                    onClick={() => handleUpdateQuantities(side)}
                    disabled={updatingQuantities !== null || !hasChanges}
                >
                    {updatingQuantities === side ? 'Updating…' : `Update ${side === 'buy' ? 'Buy' : 'Sell'} Orders`}
                </Button>
            </div>
        );
    };

    const renderClientRows = (orders: EtfClientOrder[], side: OrderSide) =>
        orders.map(order => {
            const sel = side === 'buy' ? selectedBuyClient : selectedSellClient;
            const edit = pendingEdits.get(order.id);
            const rowError = rowErrors.get(order.id);
            const fieldValue = (field: SizeField): string | number => {
                if (!edit) return (field === 'quantity' ? order.quantity : order.amount) ?? '';
                return edit.field === field ? edit.text : derivedValue(order, edit, field);
            };
            const inputClass = (field: SizeField) =>
                `${styles['qty-input']} ${edit?.field === field ? styles['qty-input-changed'] : ''} ${rowError ? styles['qty-input-invalid'] : ''}`;

            return (
                <tr key={`${side}-${order.id}`}>
                    <td className={styles['col-check']}>
                        <input type="checkbox" checked={sel.has(order.id)} onChange={() => handleClientSelect(order.id, side)} />
                    </td>
                    <td><SideTag side={side} colored>{side === 'buy' ? 'Buy' : 'Sell'}</SideTag></td>
                    <td>{formatDate(order.order_date)}</td>
                    <td>{order.scheme?.code ?? '—'}</td>
                    <td>{order.company?.symbol ?? '—'}</td>
                    <td>{order.client?.code ?? '—'}</td>
                    <td>{order.client?.name ?? '—'}</td>
                    <td className={styles.num}>
                        <input
                            type="number"
                            min="0"
                            step={isFractional(order) ? 'any' : '1'}
                            value={fieldValue('quantity')}
                            onChange={e => handleSizeChange(order.id, side, 'quantity', e.target.value)}
                            className={inputClass('quantity')}
                            aria-label="Quantity"
                            aria-invalid={rowError !== undefined}
                        />
                        {rowError && <span className={styles['qty-row-error']} role="alert">{rowError}</span>}
                    </td>
                    <td className={styles.num}>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={fieldValue('amount')}
                            onChange={e => handleSizeChange(order.id, side, 'amount', e.target.value)}
                            className={inputClass('amount')}
                            aria-label={`Amount${order.scheme?.currency ? ` (${order.scheme.currency})` : ''}`}
                            aria-invalid={rowError !== undefined}
                        />
                        <span className={styles['qty-amount-display']} aria-hidden="true">
                            {formatSummaryAmount(fieldValue('amount'), order.scheme?.currency ?? null)}
                        </span>
                    </td>
                    <td><OrderStatusPill stage={calculatedOrderStage(order.order_status)} label={order.order_status_display} /></td>
                </tr>
            );
        });

    return (
        <>
            {(successMessage || warning || error) && (
                <div className={`${styles.messages} ${styles['section-messages']}`}>
                    {successMessage && <Note tone="success">{successMessage}</Note>}
                    {warning && <Note tone="warning">{warning}</Note>}
                    {error && <Note tone="error">{error}</Note>}
                </div>
            )}
        <div className={styles['order-section']}>
            <div className={styles['order-section-header']}>
                <div className={styles['order-section-left']}>
                    <div className={styles['order-section-accent']} />
                    <h2 className={styles['order-section-title']}>All Client Orders</h2>
                    <span className={styles['badge-client-wise']}>Client-wise</span>
                </div>
            </div>

            {allEmpty ? (
                <div className={styles.empty}>
                    <Placeholder message={<>No calculated {titlePrefix ? `${titlePrefix} ` : ''}orders found.</>} />
                </div>
            ) : (
                <>
                    <div className={styles.toolbar}>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                            {renderSideControls('buy')}
                            {renderSideControls('sell')}
                        </div>
                        <span className={styles['toolbar-info']}>
                            Edit quantity or amount. Buys are capped at available cash, sells at holding.
                        </span>
                    </div>

                    <TableWrapper>
                        <table className={`${shared.table} ${styles.table}`}>
                            <thead>
                                <tr>
                                    <th className={styles['col-check']}></th>
                                    <th>Side</th>
                                    <th>Order Date</th>
                                    <th>Scheme</th>
                                    <th>Symbol</th>
                                    <th>Client Code</th>
                                    <th>Client Name</th>
                                    <th className={styles.num}>Quantity</th>
                                    <th className={styles.num}>Amount</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renderClientRows(clientBuyOrders, 'buy')}
                                {renderClientRows(clientSellOrders, 'sell')}
                            </tbody>
                        </table>
                    </TableWrapper>
                </>
            )}
        </div>
        </>
    );
};

export default ClientWiseOrdersSection;